import { useEffect, useState } from 'react';
import { Paper } from '@mui/material';
import {
  BulkDeleteWithConfirmButton,
  Button,
  DeleteWithConfirmButton,
  FunctionField,
  ListControllerResult,
  TextField,
  WrapperField,
  useListContext,
  useRecordContext,
} from 'react-admin';
import AddIcon from '@mui/icons-material/Add';

import { usePermissions } from 'src/hooks';
import { CompanySubscriptionRecord } from 'src/types';
import CraftStandaloneList from 'src/components/CraftStandaloneList';
import { useAddCompanySubscriptionsDialogContext } from '../AddCompanySubscriptionsDialog';
import IntegerInput from 'src/inputs/IntegerInput';
import { Center } from 'src/components/Center';

const getSubDeleteDialogTitle = (rec: CompanySubscriptionRecord) =>
  `Delete subscription for company #${rec.company_id}?`;

interface ListContextConsumerProps {
  setContext: React.Dispatch<React.SetStateAction<ListControllerResult | undefined>>;
}

const ListContextConsumer = ({ setContext }: ListContextConsumerProps) => {
  const listCtx = useListContext();

  useEffect(() => {
    setContext(listCtx);
  }, []);

  return null;
};

export const SubscriptionsList = () => {
  const dataPkgRecord = useRecordContext<CompanySubscriptionRecord>();
  const addSubDialogCtx = useAddCompanySubscriptionsDialogContext();
  const { permissions } = usePermissions();

  const [listCtx, setListCtx] = useState<ListControllerResult>();

  useEffect(() => {
    if (!listCtx) return;
    addSubDialogCtx.setOnSuccess(listCtx.refetch);
  }, [listCtx]);

  const filters = [<IntegerInput key="id" label="Company ID" source="company_id" alwaysOn />];

  const Actions = permissions.has('subscription:edit') && (
    <Button label="Add" startIcon={<AddIcon />} onClick={addSubDialogCtx.setOpen} />
  );

  return (
    <Paper sx={{ width: '100%', p: 2 }}>
      <CraftStandaloneList
        resource="subscriptions"
        filter={{ customer_data_package_id: dataPkgRecord.id, all_companies: false }}
        filters={filters}
        actions={Actions}
        dataGridProps={{
          empty: <Center m={4}>No company subscriptions found</Center>,
          bulkActionButtons: permissions.has('subscription:delete') && (
            <BulkDeleteWithConfirmButton mutationMode="pessimistic" />
          ),
        }}
      >
        <TextField label="Company ID" source="company_id" />
        <WrapperField label="Actions">
          <FunctionField
            render={(rec: CompanySubscriptionRecord) => (
              <DeleteWithConfirmButton
                confirmTitle={getSubDeleteDialogTitle(rec)}
                redirect={false}
                disabled={!permissions.has('subscription:delete')}
              />
            )}
          />
        </WrapperField>

        <ListContextConsumer setContext={setListCtx} />
      </CraftStandaloneList>
    </Paper>
  );
};

export default SubscriptionsList;
